import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { queryKeys } from './queryKeys';

export function useCrmStats() {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: queryKeys.crm.stats,
    queryFn: async () => {
      const response = await authFetch('/api/crm/stats');
      if (!response.ok) throw new Error('Failed to load CRM stats');
      return response.json();
    },
  });
}

export function useCrmContacts(page = 1, limit = 50, stage = null, search = null) {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: [...queryKeys.crm.contacts, page, limit, stage, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (stage && stage !== 'all') {
        params.append('stage', stage);
      }
      if (search) {
        params.append('search', search);
      }
      const response = await authFetch(`/api/crm/contacts?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to load contacts');
      return response.json();
    },
  });
}

export function useCrmContact(contactId) {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: queryKeys.crm.contact(contactId),
    queryFn: async () => {
      const response = await authFetch(`/api/crm/contact?id=${contactId}`);
      if (!response.ok) throw new Error('Failed to load contact');
      return response.json();
    },
    enabled: !!contactId,
  });
}

export function useCrmOpportunities(page = 1, limit = 50, stage = null, contactId = null, search = null) {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: [...queryKeys.crm.opportunities, page, limit, stage, contactId, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (stage && stage !== 'all') {
        params.append('stage', stage);
      }
      if (contactId) {
        params.append('contactId', contactId);
      }
      if (search) {
        params.append('search', search);
      }
      const response = await authFetch(`/api/crm/opportunities?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to load opportunities');
      return response.json();
    },
  });
}

export function useCrmOpportunity(opportunityId) {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: queryKeys.crm.opportunity(opportunityId),
    queryFn: async () => {
      const response = await authFetch(`/api/crm/opportunity?id=${opportunityId}`);
      if (!response.ok) throw new Error('Failed to load opportunity');
      return response.json();
    },
    enabled: !!opportunityId,
  });
}

export function useCrmSourceOptions(includeInactive = false) {
  const { authFetch } = useAuthContext();

  return useQuery({
    queryKey: [...queryKeys.crm.sourceOptions, includeInactive],
    queryFn: async () => {
      const params = includeInactive ? '?includeInactive=true' : '';
      const response = await authFetch(`/api/crm/source-options${params}`);
      if (!response.ok) throw new Error('Failed to load source options');
      return response.json();
    },
  });
}

export function useCrmMyAccess() {
  const { authFetch, user } = useAuthContext();

  return useQuery({
    queryKey: queryKeys.crm.myAccess,
    queryFn: async () => {
      const response = await authFetch('/api/crm/my-access');
      if (!response.ok) throw new Error('Failed to load CRM access');
      return response.json();
    },
    enabled: !!user, // Only run when user is authenticated
  });
}
